import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { Tooltip } from 'react-tooltip';

// DataObjects.
import { UserCustomerDataObject } from '@exonet/jsonapi-data-collections';

// HOC.
import boost from 'app/client/utilities/hoc/boost';

// Components.
import UserAvatar from 'app/client/components/UserAvatar';
import Badge from 'app/client/components/Badge';
import RoleBadge from 'app/client/components/RoleBadge';
import Skeleton from '../Skeleton';

import { extractUserCustomerData } from '../../helpers';
import i18n from '../../i18n';
import fetcher from './fetcher';
import store from './store';
import * as S from '../../styled';

const envelopeIcon = byPrefixAndName.fas.envelope;
const phoneIcon = byPrefixAndName.fas.phone;
const starIcon = byPrefixAndName.fas.star;

const Content = ({ userCustomer }) => {
  const { t } = useTranslation();
  const [emailsExpanded, setEmailsExpanded] = useState(false);
  const [phonesExpanded, setPhonesExpanded] = useState(false);

  const toggleEmails = useCallback((event) => {
    event.stopPropagation();
    setEmailsExpanded((prev) => !prev);
  }, []);

  const togglePhones = useCallback((event) => {
    event.stopPropagation();
    setPhonesExpanded((prev) => !prev);
  }, []);

  if (!userCustomer) return null;

  const name = userCustomer.getName?.() || '';
  const username = userCustomer.getUsername?.() || '';
  const isEnabled = userCustomer.getEnabled?.() ?? true;
  const isOwner = userCustomer.getOwner?.() || false;
  const hasPortalAccess = userCustomer.getLogin?.() ?? true;
  const externalCompany = userCustomer.getExternalCompany?.() || null;

  const userId = userCustomer.hasUserRelation?.() ? userCustomer.getUserRelation().id : null;
  const userUrl = userId ? `/customer/users/${userId}` : null;
  const cardKey = `uhc-content-${userCustomer.id?.() || userId || name}`.replace(
    /[^a-zA-Z0-9_-]/g,
    '',
  );

  const { emails, phones, roles, hasContact } = extractUserCustomerData(userCustomer);

  const visibleEmail = emails[0] || null;
  const visiblePhone = phones[0] || null;
  const hiddenEmails = emails.slice(1);
  const hiddenPhones = phones.slice(1);

  const nameNode = userUrl ? <S.NameLink to={userUrl}>{name}</S.NameLink> : name;
  const usernameNode =
    username && userUrl ? <S.UsernameLink to={userUrl}>{username}</S.UsernameLink> : username;

  return (
    <S.Card>
      <S.Header>
        <UserAvatar name={name} email={username} size="2.5rem" />
        <S.HeaderInfo>
          <S.Name>{nameNode}</S.Name>
          {username && <S.Username>{usernameNode}</S.Username>}
        </S.HeaderInfo>
      </S.Header>

      <S.StatusLine>
        {!isEnabled && (
          <Badge color="danger" icon="xmark">
            {t('userHoverCard:inactive')}
          </Badge>
        )}
        <span
          id={`${cardKey}-portal`}
          data-tooltip-content={
            hasPortalAccess ? t('userHoverCard:hasPortalAccess') : t('userHoverCard:noPortalAccess')
          }
        >
          <Badge
            color={hasPortalAccess ? 'success' : 'neutral'}
            icon={hasPortalAccess ? 'check' : 'xmark'}
          >
            {t('userHoverCard:portal')}
          </Badge>
        </span>
        <Tooltip
          anchorSelect={`#${cardKey}-portal`}
          content={
            hasPortalAccess ? t('userHoverCard:hasPortalAccess') : t('userHoverCard:noPortalAccess')
          }
          positionStrategy="fixed"
          style={{ zIndex: 9999 }}
        />
        {isOwner && (
          <>
            <span id={`${cardKey}-owner`}>
              <Badge color="warning" icon="user-crown">
                {t('userHoverCard:owner')}
              </Badge>
            </span>
            <Tooltip
              anchorSelect={`#${cardKey}-owner`}
              content={t('userHoverCard:ownerTooltip')}
              positionStrategy="fixed"
              style={{ zIndex: 9999 }}
            />
          </>
        )}
        {externalCompany && (
          <>
            <span id={`${cardKey}-external`}>
              <Badge color="info" icon="briefcase">
                {t('userHoverCard:external')}
              </Badge>
            </span>
            <Tooltip
              anchorSelect={`#${cardKey}-external`}
              content={externalCompany}
              positionStrategy="fixed"
              style={{ zIndex: 9999 }}
            />
          </>
        )}
      </S.StatusLine>

      {hasContact && (
        <>
          <S.Separator />
          <S.ContactList>
            {visibleEmail && (
              <S.ContactItem>
                <S.ContactIcon>
                  <FontAwesomeIcon icon={envelopeIcon} />
                </S.ContactIcon>
                <S.ContactValue>{visibleEmail.value}</S.ContactValue>
                {visibleEmail.isPrimary && hiddenEmails.length > 0 && (
                  <S.PrimaryBadge>
                    <FontAwesomeIcon icon={starIcon} />
                  </S.PrimaryBadge>
                )}
              </S.ContactItem>
            )}
            {emailsExpanded &&
              hiddenEmails.map((email) => (
                <S.ContactItem key={email.value} $muted>
                  <S.ContactIcon>
                    <FontAwesomeIcon icon={envelopeIcon} />
                  </S.ContactIcon>
                  <S.ContactValue>{email.value}</S.ContactValue>
                </S.ContactItem>
              ))}
            {hiddenEmails.length > 0 && (
              <S.ShowMoreButton type="button" onClick={toggleEmails}>
                {emailsExpanded
                  ? t('userHoverCard:showLess')
                  : t('userHoverCard:showMore', { count: hiddenEmails.length })}
              </S.ShowMoreButton>
            )}
            {visiblePhone && (
              <S.ContactItem>
                <S.ContactIcon>
                  <FontAwesomeIcon icon={phoneIcon} />
                </S.ContactIcon>
                <S.ContactValue>{visiblePhone.value}</S.ContactValue>
                {visiblePhone.isPrimary && hiddenPhones.length > 0 && (
                  <S.PrimaryBadge>
                    <FontAwesomeIcon icon={starIcon} />
                  </S.PrimaryBadge>
                )}
              </S.ContactItem>
            )}
            {phonesExpanded &&
              hiddenPhones.map((phone) => (
                <S.ContactItem key={phone.value} $muted>
                  <S.ContactIcon>
                    <FontAwesomeIcon icon={phoneIcon} />
                  </S.ContactIcon>
                  <S.ContactValue>{phone.value}</S.ContactValue>
                </S.ContactItem>
              ))}
            {hiddenPhones.length > 0 && (
              <S.ShowMoreButton type="button" onClick={togglePhones}>
                {phonesExpanded
                  ? t('userHoverCard:showLess')
                  : t('userHoverCard:showMore', { count: hiddenPhones.length })}
              </S.ShowMoreButton>
            )}
          </S.ContactList>
        </>
      )}

      {roles.length > 0 && (
        <>
          <S.Separator />
          <S.SectionTitle>{t('userHoverCard:roles')}</S.SectionTitle>
          <S.RolesWrap>
            {roles.map((role) => (
              <RoleBadge
                key={`${cardKey}-role-${role.id}`}
                id={role.id}
                name={role.name}
                isExonet={role.isExonet}
              />
            ))}
          </S.RolesWrap>
        </>
      )}
    </S.Card>
  );
};

Content.propTypes = {
  userCustomer: PropTypes.instanceOf(UserCustomerDataObject),
};

export default boost({
  i18n,
  fetcher,
  store,
  loader: Skeleton,
})(Content);
